import { AfterViewInit, Component, ViewEncapsulation, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PORT_LIST } from 'src/app/shared/data/port-list';
import { MAKE_LIST } from 'src/app/shared/data/make-list';
import { MF_YEAR_LIST, REG_YEAR_LIST } from 'src/app/shared/data/years-list';
import { MONTH_LIST } from 'src/app/shared/data/month-list';
import { FUEL_LIST } from 'src/app/shared/data/fuel-list';
import { COLOR_LIST } from 'src/app/shared/data/color-list';

import { STOCK_CHARGES_LIST } from 'src/app/shared/data/charges-list';
import { Vehicle, Charge } from '../proforma.model';
import { CompanyService } from 'src/app/services/company/company.service';
import { PortService } from 'src/app/services/port/port.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';

import { OrderService } from 'src/app/services/order/order.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { Router } from '@angular/router';
import { InvoiceOrder } from 'src/app/shared/models';
import { formatDate } from '@angular/common';

declare var Swal: any;

interface Item {
  description: string;
  quantity: number;
  currency: string;
  amount: number;
}


@Component({
  selector: 'app-proforma-edit',
  templateUrl: './proforma-edit.component.html',
  styleUrls: ['./proforma-edit.component.scss']
})
export class ProformaEditComponent extends DestructorComponent implements OnInit, AfterViewInit{

  countryList: any[] = [];
  portList = PORT_LIST;
  toPortList: any[] = []; // Dynamic port list based on selected country
  maketList: any[] = []; // Will be populated from API
  mfYearsList = MF_YEAR_LIST;
  regYearsList = REG_YEAR_LIST;
  mfMonthList = MONTH_LIST;
  regMonthList = MONTH_LIST;
  fueltList:any = [];
  extColortList = COLOR_LIST;

  proformaId: string = '';
  proforma: any = null;

  items: Item[] = [];
  vehicles: Vehicle[] = [];
  charges: Charge[] = [];

  // Track which vehicles were loaded from proforma (not newly added)
  loadedVehicleIds: Set<string> = new Set();

  currencyList = ['USD', 'JPY'];

  chargesList = STOCK_CHARGES_LIST;

  // Form Group
  proformaForm: FormGroup;

  // Company search properties
  companySearchTerm: string = '';
  companySearchResults: any[] = [];
  isCompanySearching: boolean = false;
  showCompanyDropdown: boolean = false;
  selectedCompany: any = null;
  private companySearchSubject = new Subject<string>();




  // Item description properties
  isDescriptionAutoGenerated: boolean = false;

  // Form submission tracking
  isFormSubmitted: boolean = false;
  isLoading: boolean = false;

  // Current vehicle index for modal operations
  currentVehicleIndex: number = 0;

  // Consignee modal properties
  selectedCompanyForConsignee: any = null;
  selectedCompanyName: string = '';

  constructor(
    private _companyService: CompanyService,
    private _portService: PortService,
    private _generalService: GeneralService,
    private _globalStateService: GlobalStateService,

    private _orderService: OrderService,
    private _router: Router,
    private fb: FormBuilder
  ) {
    super();
    this.setupCompanySearchDebounce();
    this.initializeForm();
  }

  ngOnInit(): void {
    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
        console.log('Countries loaded in proforma:', this.countryList);
      })
    );

    this.proformaId = this._router.url.split('/').pop() || '';

    this.loadProformaData();

    // Subscribe to settings from GlobalStateService to auto-populate shipper information
    this.subscriptions.push(
      this._globalStateService.GetSetting().subscribe(setting => {
        if (setting && setting.shipper_information) {
          this.autoPopulateShipperInformation(setting.shipper_information);
          this.fueltList = setting.fuel_types;
        }
      })
    );

    // Load makes from API
    this.loadMakes();
  }

  private loadProformaData(): void {
    this.subscriptions.push(
      this._orderService.GetDataById(this.proformaId).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.proforma = response.data;
            this.onToCountryChange("",this.proforma.to_country);
            this.populateFormWithProformaData();
            console.log('Proforma data loaded:', this.proforma);
          }
        },
        error: (error: any) => {
          console.error('Error loading proforma data:', error);
        }
      })
    );
  }




  private populateFormWithProformaData(): void {
    if (!this.proforma) return;

    // Set the company search term to display in the input field
    this.companySearchTerm = this.proforma.buyer_company_name || '';

    // Set discount percent
    this.discountPercent = this.proforma.discount_percent || 0;

    // Populate form fields with proforma data
    this.proformaForm.patchValue({
      proformaNumber: this.proforma.order_number,
      proformaDate: formatDate(this.proforma.order_date, 'yyyy-MM-dd', 'en-US'),
      buyerCompanyName: this.proforma.buyer_company_name,
      buyerTitle: this.proforma.buyer_title,
      buyerName: this.proforma.buyer_consignee_name,
      buyerAddress: this.proforma.buyer_street_address,
      buyerCity: this.proforma.buyer_city,
      buyerCountry: this.proforma.buyer_country,
      buyerId: this.proforma.buyer_id,
      buyerEmail: this.proforma.buyer_email_address,
      buyerPhone1: this.proforma.buyer_telephone_no,
      buyerPhone2: this.proforma.buyer_phone2,
      fromCountry: this.proforma.from_country,
      fromPort: this.proforma.from_port,
      toCountry: this.proforma.to_country,
      toPort: this.proforma.to_port,
      paymentTerms: this.proforma.payment_terms,
      shipType: this.proforma.ship_type,
      totalType: this.proforma.total_type,
      agentNotes: this.proforma.agent_notes,
      discountPercent: this.proforma.discount_percent || 0
    });

    // Populate vehicles and items
    this.populateVehiclesAndItems();

    // Populate charges from proforma data
    this.populateCharges();
  }

  private populateVehiclesAndItems(): void {
    if (!this.proforma) return;

    this.proforma.vehicles.forEach((vehicle: any, index: number) => {
      let filledVehicle = this.getFilledVehicle(vehicle);
      console.log({filledVehicle})
      this.vehicles.push(filledVehicle);
      this.items.push(this.proforma.items[index]);
    });
    this.addFilledTotal(this.proforma.items);
  }

  private populateCharges(): void {
    if (!this.proforma) return;

    // Clear existing charges
    this.charges = [];

    // Populate charges from proforma data if available
    if (this.proforma.charges && Array.isArray(this.proforma.charges) && this.proforma.charges.length > 0) {
      this.proforma.charges.forEach((charge: any) => {
        this.charges.push({
          currency: charge.currency || 'JPY',
          charge_type: charge.charge_type || '',
          amount: charge.amount || 0
        });
      });
      console.log('Charges loaded from proforma:', this.charges);
    } else {
      // If no charges in proforma, initialize with one empty charge
      this.addCharge();
    }
  }


  addFilledTotal(items: Item[]): void {
    const total = items.reduce((sum, item) => sum + (+item.amount), 0);
    const discountAmount = total * (this.discountPercent / 100);
    const final = total - discountAmount;
    console.log({total, discountAmount, final});
    this.formattedFinalTotal = this.formatNumberWithCommas(final.toFixed(2));
    this.formattedTotal = this.formatNumberWithCommas(total.toFixed(2));
  }

  private loadMakes(): void {
    this.subscriptions.push(
      this._generalService.GetMakes().subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Map API response to expected format with name as label and slug as value
            this.maketList = response.data.map((make: any) => ({
              value: make.slug || make._id, // Use slug as value, fallback to _id
              label: make.name
            }));
            console.log('Makes loaded from API:', this.maketList);
          } else {
            console.warn('Invalid makes API response:', response);
            // Fallback to static data if API fails
            this.maketList = MAKE_LIST;
          }
        },
        error: (error: any) => {
          console.error('Error loading makes from API:', error);
          // Fallback to static data if API fails
          this.maketList = MAKE_LIST;
        }
      })
    );
  }





  initializeForm(): void {
    this.proformaForm = this.fb.group({
      // Proforma Header - Auto-generated, no validation required
      proformaNumber: [''],
      proformaDate: [''],

      // Buyer Information
      buyerId: [''],
      buyerCompanyName: ['', Validators.required, ],
      buyerTitle: [{value:'Mr.',disabled:true}, Validators.required],
      buyerName: ['', Validators.required],
      buyerAddress: ['', Validators.required],
      buyerCity: ['', Validators.required],
      buyerCountry: ['', Validators.required],
      buyerEmail: ['', [Validators.required, Validators.email]],
      buyerPhone1: ['', Validators.required],
      buyerPhone2: [''],
      sameAsConsignee: [false],

      // Shipper Information - Static/Read-only, no validation required
      shipperCompanyName: [''],
      shipperAddress1: [''],
      shipperAddress2: [''],
      shipperCity: [''],
      shipperState: [''],
      shipperCountry: [''],
      shipperZipCode: [''],
      shipperFax: [''],
      shipperPhone: [''],
      fromCountry: ['109'], // Default Japan
      fromPort: ['Any Port In Japan'], // Default ANY PORT IN JAPAN
      toCountry: ['', Validators.required],
      toPort: ['', Validators.required],
      paymentTerms: [''],

      // Additional fields
      discountPercent: [0],
      shipType: ['', Validators.required],
      totalType: ['', Validators.required],
      agentNotes: ['']
    });
  }

  /**
   * Auto-populate shipper information from global settings
   * Maps setting fields to form control names
   * Only populates empty fields to avoid overwriting user input
   */
  autoPopulateShipperInformation(shipperInfo: any): void {
    if (!shipperInfo) return;

    // Get current form values to check which fields are already filled
    const currentFormValues = this.proformaForm.value;

    // Map shipper information fields to form controls
    const shipperFormData: any = {};

    // Map available fields from shipper_information to form fields
    // Only populate if the form field is currently empty
    if (shipperInfo.company_name && !currentFormValues.shipperCompanyName) {
      shipperFormData.shipperCompanyName = shipperInfo.company_name;
    }
    if (shipperInfo.address_1 && !currentFormValues.shipperAddress1) {
      shipperFormData.shipperAddress1 = shipperInfo.address_1;
    }
    if (shipperInfo.address_2 && !currentFormValues.shipperAddress2) {
      shipperFormData.shipperAddress2 = shipperInfo.address_2;
    }
    if (shipperInfo.city && !currentFormValues.shipperCity) {
      shipperFormData.shipperCity = shipperInfo.city;
    }
    if (shipperInfo.state && !currentFormValues.shipperState) {
      shipperFormData.shipperState = shipperInfo.state;
    }
    if (shipperInfo.country && !currentFormValues.shipperCountry) {
      shipperFormData.shipperCountry = shipperInfo.country;
    }
    if (shipperInfo.zip && !currentFormValues.shipperZipCode) {
      shipperFormData.shipperZipCode = shipperInfo.zip;
    }
    if (shipperInfo.telephone_number && !currentFormValues.shipperPhone) {
      shipperFormData.shipperPhone = shipperInfo.telephone_number;
    }
    if (shipperInfo.payment_terms && !currentFormValues.paymentTerms) {
      shipperFormData.paymentTerms = shipperInfo.payment_terms;
    }

    // Only update the form if there are fields to populate
    if (Object.keys(shipperFormData).length > 0) {
      this.proformaForm.patchValue(shipperFormData);

      console.log('Shipper information auto-populated:', {
        originalData: shipperInfo,
        mappedData: shipperFormData,
        fieldsPopulated: Object.keys(shipperFormData)
      });
    } else {
      console.log('No shipper fields auto-populated (all fields already have values or no matching data)');
    }
  }

  getEmptyVehicle(): Vehicle {
    return {
      selectAll: false,
      stockId: '',
      stockMake: '',
      stockMakeNew: '',
      stockModel: '',
      stockModelNew: '',
      stockModelCode: '',
      stockChassisNo: '',
      stockMFYear: '',
      stockCarNo: '',
      stockMFMonth: '',
      stockRegYear: '',
      stockInventoryNumber: '',
      stockRegMonth: '',
      stockFuel: '',
      stockFuelNew: '',
      stockExteriorColor: '',
      stockExteriorColorNew: '',
      stockSteering: '',
      stockBodyType: '',
      stockHSCode: '',
      stockEngineNo: '',
      stockTransmission: '',
      stockTransmissionNew: '',
      stockMileage: '',
      stockSeatCapacity: '',
      stockAuctionHall: '',
      stockLotNo: '',
      stockOptions: '',
      files: []
    };
  }

  getFilledVehicle(vehicle: any): Vehicle {
    console.log('Raw vehicle data from API:', vehicle);

    // Track this vehicle as loaded from proforma
    if (vehicle._id) {
      this.loadedVehicleIds.add(vehicle._id);
    }

    // Handle accessories_and_options - convert object to comma-separated string
    let optionsString = '';
    if (vehicle.accessories_and_options && typeof vehicle.accessories_and_options === 'object') {
      optionsString = Object.keys(vehicle.accessories_and_options)
        .filter(key => vehicle.accessories_and_options[key] === 1)
        .join(', ');
    } else if (vehicle.stockOptions && typeof vehicle.stockOptions === 'string') {
      optionsString = vehicle.stockOptions;
    }

    // Handle seat capacity - convert to string if it's a number
    let seatCapacity = '';
    if (vehicle.seat_capacity) {
      seatCapacity = typeof vehicle.seat_capacity === 'number' ? vehicle.seat_capacity.toString() : vehicle.seat_capacity;
    } else if (vehicle.seating_capacity) {
      seatCapacity = typeof vehicle.seating_capacity === 'number' ? vehicle.seating_capacity.toString() : vehicle.seating_capacity;
    }

    const filledVehicle = {
      selectAll: false,
      stockId: vehicle._id,
      stockMake: vehicle.make || vehicle.company_name,
      stockMakeNew: vehicle.new_fields?.make || '',
      stockModel: vehicle.model || vehicle.name,
      stockModelNew: vehicle.new_fields?.model || '',
      stockModelCode: vehicle.model_code || '',
      stockChassisNo: vehicle.chassis_no || vehicle.vin_no,
      stockMFYear: vehicle.year,
      stockMFMonth: vehicle.month,
      stockRegYear: vehicle.reg_year || vehicle.year,
      stockInventoryNumber: vehicle.inventory_no,
      stockCarNo: vehicle.car_no,
      stockRegMonth: vehicle.reg_month,
      stockFuel: vehicle.fuel_type_slug,
      stockFuelNew: '',
      stockExteriorColor: vehicle.exterior_color,
      stockExteriorColorNew: '',
      stockSteering: vehicle.steering,
      stockBodyType: vehicle.body_type,
      stockHSCode: vehicle.hs_code,
      stockEngineNo: vehicle.engine,
      stockTransmission: vehicle.transmission_slug,
      stockTransmissionNew: vehicle.new_fields?.transmission || '',
      stockMileage: vehicle.mileage ? vehicle.mileage.toString() : '',
      stockSeatCapacity: seatCapacity,
      stockAuctionHall: vehicle.auction_hall || vehicle.hall_name || '',
      stockLotNo: vehicle.car_no || vehicle.lot_no || '',
      stockOptions: optionsString,
      files: []
    };

    console.log('Mapped vehicle data:', {
      raw: {
        seat_capacity: vehicle.seat_capacity,
        seating_capacity: vehicle.seating_capacity,
        auction_hall: vehicle.auction_hall,
        hall_name: vehicle.hall_name,
        stock_no: vehicle.stock_no,
        lot_no: vehicle.lot_no,
        stockOptions: vehicle.stockOptions,
        accessories_and_options: vehicle.accessories_and_options
      },
      mapped: {
        stockSeatCapacity: filledVehicle.stockSeatCapacity,
        stockAuctionHall: filledVehicle.stockAuctionHall,
        stockLotNo: filledVehicle.stockLotNo,
        stockOptions: filledVehicle.stockOptions
      }
    });

    return filledVehicle;
  }



  addVehicle(): void {
    const vehicle = this.getEmptyVehicle();
    this.vehicles.push(vehicle);

    // Add a new item for this vehicle
    // this.addItem();
  }

  removeVehicle(index: number): void {
    index = index+1;

    console.log('removeVehicle called with index:', index);
    console.log('Current vehicles array length:', this.vehicles.length);
    console.log('Vehicle to remove:', this.vehicles[index]);

    if (this.vehicles.length > 1) {
      // Remove the vehicle ID from loaded set if it was loaded
      const vehicleId = this.vehicles[index]?.stockId;
      console.log('Vehicle ID to remove from tracking:', vehicleId);

      if (vehicleId && this.loadedVehicleIds.has(vehicleId)) {
        this.loadedVehicleIds.delete(vehicleId);
        console.log('Removed vehicle ID from loadedVehicleIds');
      }

      this.vehicles.splice(index, 1);
      console.log('Vehicle removed. New vehicles array length:', this.vehicles.length);

      // Remove corresponding item if it exists
      if (this.items.length > index && this.items.length > 1) {
        this.removeItem(index);
        console.log('Corresponding item removed');
      }
    }
  }

  // Check if a vehicle was loaded from the proforma (not newly added)
  isVehicleFromProforma(vehicle: Vehicle): boolean {
    return vehicle.stockId ? this.loadedVehicleIds.has(vehicle.stockId) : false;
  }

  // TrackBy function for vehicles ngFor to help Angular track items correctly
  trackByVehicleIndex(index: number, vehicle: Vehicle): any {
    // Use stockId if available, otherwise use a combination of index and chassis number
    // This ensures each vehicle has a unique identifier for Angular's change detection
    return vehicle.stockId || vehicle.stockChassisNo || `vehicle-${index}-${vehicle.stockMake}-${vehicle.stockModel}`;
  }



  resetVehicle(index: number): void {
    this.vehicles[index] = this.getEmptyVehicle();
  }

  editVehicle(vehicleId: string): void {
    if (vehicleId) {
      // Navigate to the vehicle edit page in a new tab
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['/vehicles/vehicle-manager/edit', vehicleId])
      );
      window.open(url, '_blank');
    }
  }

  openCarListingModal(vehicleIndex: number): void {
    // Store the current vehicle index for later use when selecting from stock
    this.currentVehicleIndex = vehicleIndex;

    // Open the car listing modal
    const modalElement = document.getElementById('kt_modal_car_listing');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onCarsSelected(selectedCars: any[]): void {

    // Add selected cars as items to the calculation section
    this.addCarsAsItems(selectedCars);

    // Map selected cars to vehicle sections
    this.mapSelectedCarsToVehicles(selectedCars);
  }

  private mapSelectedCarsToVehicles(selectedCars: any[]): void {
    selectedCars.forEach((car, index) => {
      let targetVehicleIndex = this.currentVehicleIndex + index;

      // Ensure we have enough vehicle sections
      // Don't add items here since they're already added in addCarsAsItems()
      while (this.vehicles.length <= targetVehicleIndex) {
        const vehicle = this.getEmptyVehicle();
        this.vehicles.push(vehicle);
      }

      // Map the car data to the vehicle using the same logic as the existing implementation
      this.mapCarDataToVehicle(car, targetVehicleIndex);



      console.log(`Car ${index + 1} mapped to vehicle index ${targetVehicleIndex}`);
    });
  }



  private addCarsAsItems(selectedCars: any[]): void {
    // Add each selected car as a regular item in the items array
    selectedCars.forEach(car => {
      const carItem = {
        description: `${car.company_name} ${car.name} (${car.year}) - VIN: ${car.vin_no}`,
        quantity: 1,
        currency: 'JPY',
        amount: car.price || 0
      };

      this.items.push(carItem);
    });

    // Update totals after adding car items
    this.updateTotal();

  }

  toggleAllProformaFields(vehicleIndex: number): void {
    const vehicle = this.vehicles[vehicleIndex];
    if (!vehicle) return;

    if (vehicle.selectAll) {
      // Fill all fields with placeholder values to make checkboxes checked
      if (!vehicle.stockBodyType) vehicle.stockBodyType = 'N/A';
      if (!vehicle.stockHSCode) vehicle.stockHSCode = 'N/A';
      if (!vehicle.stockTransmission && !vehicle.stockTransmissionNew) vehicle.stockTransmission = 'manual';
      if (!vehicle.stockMileage) vehicle.stockMileage = '0';
      if (!vehicle.stockEngineNo) vehicle.stockEngineNo = 'N/A';
      if (!vehicle.stockSeatCapacity) vehicle.stockSeatCapacity = 'N/A';
      if (!vehicle.stockAuctionHall) vehicle.stockAuctionHall = 'N/A';
      if (!vehicle.stockLotNo) vehicle.stockLotNo = 'N/A';
      if (!vehicle.stockOptions) vehicle.stockOptions = 'N/A';
    } else {
      // Clear all fields to make checkboxes unchecked
      vehicle.stockBodyType = '';
      vehicle.stockHSCode = '';
      vehicle.stockTransmission = '';
      vehicle.stockTransmissionNew = '';
      vehicle.stockMileage = '';
      vehicle.stockEngineNo = '';
      vehicle.stockSeatCapacity = '';
      vehicle.stockAuctionHall = '';
      vehicle.stockLotNo = '';
      vehicle.stockOptions = '';
    }
  }




  formattedTotal = '0';
  formattedFinalTotal = '0';
  discountPercent: number = 0;  // Discount percentage (0 to 100)

  getTotalQuantity(): number {
    return this.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  // Format number without decimals and with commas (for input field display)
  formatNumberWithCommasNoDecimals(num: number): string {
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Format number with 2 decimals and commas (for totals)
  formatNumberWithCommas(numStr: string): string {
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts.length === 1) parts.push('00'); // Ensure 2 decimals
    if (parts[1].length === 1) parts[1] += '0'; // pad decimals if needed
    return parts.join('.');
  }

  onAmountInput(event: Event, item: any): void {
    const input = event.target as HTMLInputElement;
    // Remove commas and non-digit characters
    let raw = input.value.replace(/,/g, '').replace(/[^0-9]/g, '');
    item.amount = Number(raw) || 0;
    // Show formatted number without decimals in input
    input.value = this.formatNumberWithCommasNoDecimals(item.amount);
    this.updateTotal();
  }

  updateFinalTotal(): void {
    const total = parseFloat(this.formattedTotal.replace(/,/g, '')) || 0;
    const discountAmount = total * (this.discountPercent / 100);
    const final = total - discountAmount;
    this.formattedFinalTotal = this.formatNumberWithCommas(final.toFixed(2));
  }

  updateTotal(): void {
    let total = 0;
    for (const item of this.items) {
      const qty = Number(item.quantity) || 0;
      const amt = Number(item.amount) || 0;
      total += qty * amt;
    }
    this.formattedTotal = this.formatNumberWithCommas(total.toFixed(2));
    this.updateFinalTotal();
  }

  addItem(): void {
    const newItem = {
      description: '',
      quantity: 1,
      currency: 'USD',
      amount: 0
    };
    this.items.push(newItem);
  }

  removeItem(index: number): void {
    if (this.items.length > 1) {
      this.items.splice(index, 1);
      this.updateTotal();
    }
  }

  // Charge management methods
  addCharge(): void {
    const newCharge: Charge = {
      currency: 'JPY',
      charge_type: '',
      amount: 0
    };
    this.charges.push(newCharge);
  }

  removeCharge(index: number): void {
    if (this.charges.length > 0) {
      this.charges.splice(index, 1);
    }
  }

  onChargeTypeChange(index: number, selectedValue: string): void {
    if (selectedValue && this.charges[index]) {
      const selectedCharge = this.chargesList.find(charge => charge.value === selectedValue);
      if (selectedCharge) {
        this.charges[index].charge_type = selectedCharge.value || selectedCharge.label;
      }
    }
  }

  onChargeCurrencyChange(index: number, currency: string): void {
    if (this.charges[index]) {
      this.charges[index].currency = currency;
    }
  }

  formatPrice(value: string): string {
    const num = Number(value);
    return isNaN(num) ? '' : num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  // Company search methods
  private setupCompanySearchDebounce(): void {
    this.companySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (searchTerm.length >= 2) {
        this.searchCompanies(searchTerm);
      } else {
        this.companySearchResults = [];
        this.showCompanyDropdown = false;
      }
    });
  }

  onCompanySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.companySearchTerm = target.value;
    this.selectedCompany = null;

    // Update the form control with the typed value
    this.proformaForm.patchValue({
      buyerCompanyName: this.companySearchTerm
    });

    if (this.companySearchTerm.length >= 2) {
      this.companySearchSubject.next(this.companySearchTerm);
      this.showCompanyDropdown = true;
    } else {
      this.showCompanyDropdown = false;
      this.companySearchResults = [];
    }
  }

  private searchCompanies(searchTerm: string): void {
    this.isCompanySearching = true;

    this._companyService.GetData('1', searchTerm).subscribe({
      next: (response: any) => {
        this.isCompanySearching = false;
        if (response.status && response.data && response.data.data) {
          this.companySearchResults = response.data.data;
          this.showCompanyDropdown = this.companySearchResults.length > 0;
        } else {
          this.companySearchResults = [];
          this.showCompanyDropdown = false;
        }
      },
      error: (error: any) => {
        this.isCompanySearching = false;
        console.error('Error searching companies:', error);
        this.companySearchResults = [];
        this.showCompanyDropdown = false;
      }
    });
  }

  selectCompany(company: any): void {
    this.selectedCompany = company;
    const companyName = company.company || company.name || '';
    this.companySearchTerm = companyName;
    this.showCompanyDropdown = false;
    this.companySearchResults = [];

    // Update the form with company name only
    this.proformaForm.patchValue({
      buyerId: company._id || company.id || '',
      buyerCompanyName: companyName
    });

    console.log('Company selected, opening consignee modal:', {
      companyId: company._id || company.id || '',
      companyName: companyName
    });

    // Open consignee modal instead of directly populating form
    this.openConsigneeModal(company._id || company.id || '', companyName);
  }

  clearCompanySearch(): void {
    this.companySearchTerm = '';
    this.selectedCompany = null;
    this.showCompanyDropdown = false;
    this.companySearchResults = [];

    // Reset buyer information fields that were auto-filled from company selection
    this.proformaForm.patchValue({
      buyerId: '',
      buyerCompanyName: '',
      buyerTitle: 'Mr.',
      buyerName: '',
      buyerAddress: '',
      buyerCity: '',
      buyerCountry: '',
      buyerEmail: '',
      buyerPhone1: '',
      buyerPhone2: ''
    });
  }

  onCompanyInputFocus(): void {
    if (this.companySearchResults.length > 0) {
      this.showCompanyDropdown = true;
    }
  }

  onCompanyInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showCompanyDropdown = false;
    }, 200);
  }

  // Consignee modal methods
  openConsigneeModal(companyId: string, companyName: string): void {
    this.selectedCompanyForConsignee = { _id: companyId };
    this.selectedCompanyName = companyName;

    // Open the consignee modal
    const modalElement = document.getElementById('kt_modal_consignee_list');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onConsigneeSelected(consignee: any): void {
    console.log('Consignee selected:', consignee);

    // Auto-populate buyer information fields with consignee data
    this.proformaForm.patchValue({
      buyerTitle: 'Mr.', // Default title
      buyerName: consignee.name || '',
      buyerAddress: consignee.address || '',
      buyerCity: consignee.city || '',
      buyerCountry: consignee.country || '',
      buyerEmail: consignee.email || '',
      buyerPhone1: consignee.telephone || '',
      buyerPhone2: '' // Clear secondary phone
    });

    console.log('Buyer form fields auto-populated with consignee data:', {
      consigneeName: consignee.name,
      formValue: this.proformaForm.value
    });
  }









  resetStockInformation(vehicleIndex: number): void {
    if (!this.vehicles[vehicleIndex]) {
      return;
    }

    const vehicle = this.vehicles[vehicleIndex];

    // Reset all stock information fields that were populated from car data
    vehicle.stockId = '';
    vehicle.stockChassisNo = '';
    vehicle.stockMFYear = '';
    vehicle.stockRegYear = '';
    vehicle.stockMFMonth = '';
    vehicle.stockRegMonth = '';
    vehicle.stockFuel = '';
    vehicle.stockExteriorColor = '';
    vehicle.stockTransmission = '';
    vehicle.stockMileage = '';
    vehicle.stockInventoryNumber = '';

    // Reset additional stock fields
    vehicle.stockBodyType = '';
    vehicle.stockHSCode = '';
    vehicle.stockEngineNo = '';
    vehicle.stockSeatCapacity = '';
    vehicle.stockAuctionHall = '';
    vehicle.stockLotNo = '';
    vehicle.stockOptions = '';

    // Reset the item description if it was auto-generated
    if (this.isDescriptionAutoGenerated && this.items.length > 0) {
      this.items[0].description = '';
      this.isDescriptionAutoGenerated = false;
    }

    console.log('Stock information reset for vehicle index:', vehicleIndex);
  }



  mapCarDataToVehicle(carData: any, vehicleIndex: number): void {
    if (!this.vehicles[vehicleIndex]) {
      return;
    }

    const vehicle = this.vehicles[vehicleIndex];

    // Set stockId with car ID
    vehicle.stockId = carData._id || '';

    // Map basic car information with safe property access
    // make -> company_name
    vehicle.stockMake = carData.company_name || '';

    // model -> name
    vehicle.stockModel = carData.name || '';

    // model_code -> model_code_obj?.code
    vehicle.stockModelCode = carData.model_code_obj?.code || '';

    // chassis_no -> vin_no
    vehicle.stockChassisNo = carData.vin_no || carData.chassis_no || '';

    // manufacturing_year -> manufacturing_year
    vehicle.stockMFYear = carData.manufacturing_year ? carData.manufacturing_year.toString() : (carData.year ? carData.year.toString() : '');

    // manufacturing_month -> manufacturing_month
    vehicle.stockMFMonth = carData.manufacturing_month || '';

    // registration_year -> registration_year
    vehicle.stockRegYear = carData.registration_year ? carData.registration_year.toString() : (carData.year ? carData.year.toString() : '');

    // registration_month -> registration_month
    vehicle.stockRegMonth = carData.registration_month || '';

    // fuel -> fuel_type_slug
    vehicle.stockFuel = carData.fuel_type_slug || '';

    // exterior_color -> exterior_color
    vehicle.stockExteriorColor = carData.exterior_color || carData.color || '';

    // steering -> steering
    vehicle.stockSteering = carData.steering || '';

    // body_type -> body_type_obj.name
    vehicle.stockBodyType = carData.body_type_obj?.name || carData.body_type || '';

    // hs_code -> hs_code
    vehicle.stockHSCode = carData.hs_code || '';

    // transmission -> transmission_slug
    vehicle.stockTransmission = carData.transmission_slug || '';

    // mileage -> mileage
    vehicle.stockMileage = carData.mileage ? carData.mileage.toString() : '';

    // engine_no -> engine_number (prioritize engine_number over engine_no or engine)
    vehicle.stockEngineNo = carData.engine_number || carData.engine_no || '';

    // seat_capacity -> seating_capacity
    vehicle.stockSeatCapacity = carData.seating_capacity ? carData.seating_capacity.toString() : '';

    // auction_hall -> hall_name
    vehicle.stockAuctionHall = carData.hall_name || '';

    // stock_lot_no -> car_no
    vehicle.stockLotNo = carData.car_no || '';
    vehicle.stockCarNo = carData.car_no || '';

    // options -> accessories_and_options (object), show the keys which has value 1 as comma separated
    if (carData.accessories_and_options && typeof carData.accessories_and_options === 'object') {
      const options = Object.keys(carData.accessories_and_options)
        .filter(key => carData.accessories_and_options[key] === 1)
        .join(', ');
      vehicle.stockOptions = options;
    } else {
      vehicle.stockOptions = '';
    }

    // inventory_no -> inventory_no
    vehicle.stockInventoryNumber = carData.inventory_no || '';

    // Map vehicle pictures
    vehicle.pictures = carData.pictures || [];

    console.log('Car data mapped to vehicle:', {
      vehicleIndex,
      stockId: vehicle.stockId,
      make: vehicle.stockMake,
      model: vehicle.stockModel,
      chassisNo: vehicle.stockChassisNo
    });
  }

  updateProformaItems(carData: any, vehicleIndex: number): void {
    if (!this.vehicles[vehicleIndex]) {
      return;
    }

    const vehicle = this.vehicles[vehicleIndex];

    // Update vehicle fields directly with car data
    if (carData.engine_number || carData.engine_no) {
      vehicle.stockEngineNo = carData.engine_number || carData.engine_no;
    }

    if (carData.body_type) {
      vehicle.stockBodyType = carData.body_type;
    }

    console.log('Vehicle fields updated with car data:', {
      vehicleIndex,
      stockEngineNo: vehicle.stockEngineNo,
      stockBodyType: vehicle.stockBodyType
    });
  }



  // Port-related methods
  onToCountryChange(countryCode: string, countryName: string = ''): void {
    if (countryCode || countryName) {
      // Reset the port selection when country changes
      this.proformaForm.patchValue({ toPort: '' });
      this.toPortList = [];

      // Fetch ports for the selected country
      this.subscriptions.push(
        this._portService.GetData('1', '', { country_code: countryCode, country_name: countryName, paginate: false }).subscribe({
          next: (response: any) => {
            if (response.status && response.data) {
              // Handle non-paginated response
              this.toPortList = response.data.map((port: any) => ({
                value: port._id,
                label: port.name
              }));
              this.proformaForm.patchValue({ toPort: this.proforma.to_port });
            } else {
              this.toPortList = [];
            }
          },
          error: (error: any) => {
            console.error('Error fetching ports:', error);
            this.toPortList = [];
          }
        })
      );
    } else {
      // Clear ports if no country is selected
      this.toPortList = [];
      this.proformaForm.patchValue({ toPort: '' });
    }
  }

  // Form submission
  onSubmit(): void {
    // Set form as submitted to show validation errors
    this.isFormSubmitted = true;

    // Mark all form fields as touched to show validation errors
    this.proformaForm.markAllAsTouched();

    // Validate form, vehicles, and items
    const formValid = this.proformaForm.valid;
    const vehiclesValid = this.validateVehicles();
    const itemsValid = this.validateItems();

    if (formValid && vehiclesValid && itemsValid) {
      const formData = this.proformaForm.value;

      // Transform data according to the required API format
      const apiData = this.transformDataForAPI(formData);

      console.log('Proforma API Data:', apiData);

      // Show loading state
      this.isLoading = true;

      // Call OrderService AddData method
      this.subscriptions.push(
        this._orderService.UpdateData(this.proforma._id,apiData).subscribe({
          next: (response: any) => {
            this.isLoading = false;

            // Show success message
            Swal.fire({
              text: "Proforma has been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Navigate back to proforma list
                this._router.navigate(['/sales/proforma']);
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Error creating proforma:', error);

            // Show error message
            Swal.fire({
              text: "Sorry, there was an error creating the proforma. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      // Get all invalid fields and show detailed error information
      const invalidFields = this.getInvalidFields();
      const invalidVehicleFields = this.getInvalidVehicleFields();
      const invalidItemFields = this.getInvalidItemFields();

      const allInvalidFields = [...invalidFields, ...invalidVehicleFields, ...invalidItemFields];
      console.log('Form is invalid. Missing required fields:', allInvalidFields);

      // Show validation error message
      this.showValidationErrorAlert(allInvalidFields);
    }
  }

  private transformDataForAPI(formData: any): any {
    // Transform vehicles data according to API requirements
    const vehicles = this.vehicles.map((vehicle: Vehicle) => {
      return {
        _id: vehicle.stockId || '',
        make: vehicle.stockMake || '',
        mileage: parseInt(vehicle.stockMileage || '0') || 0,
        model: vehicle.stockModel || '',
        year: vehicle.stockMFYear || '',
        reg_year: vehicle.stockRegYear || '',
        inventory_no: vehicle.stockInventoryNumber || '',
        month: this.getMonthName(vehicle.stockMFMonth) || 'january',
        reg_month: this.getMonthName(vehicle.stockRegMonth) || 'january',
        chassis_no: vehicle.stockChassisNo || '',
        fuel_type: vehicle.stockFuel || 'petrol',
        engine: vehicle.stockEngineNo || '2500 CC',
        transmission: vehicle.stockTransmission || 'automatic',
        exterior_color: vehicle.stockExteriorColor || 'black',
        body_type: vehicle.stockBodyType || '',
        hs_code: vehicle.stockHSCode || '',
        seat_capacity: vehicle.stockSeatCapacity || '',
        auction_hall: vehicle.stockAuctionHall || '',
        stock_no: vehicle.stockLotNo || '',
        stockOptions: vehicle.stockOptions || '',
        steering: vehicle.stockSteering || ''
      };
    });

    // Transform items data
    const items = this.items.map(item => ({
      amount: item.amount || 0,
      currency: item.currency || 'JPY',
      description: item.description || '',
      quantity: item.quantity || 1
    }));

    // Transform charges data - only include charges that have both charge_type and amount
    const charges = this.charges
      .filter(charge => charge.charge_type && charge.amount > 0)
      .map(charge => ({
        currency: charge.currency || 'JPY',
        charge_type: charge.charge_type,
        amount: charge.amount || 0
      }));

    // Create the API payload according to the required format
    return {
      buyer_consignee_name: formData.buyerName || '',
      buyer_street_address: formData.buyerAddress || '',
      buyer_city: formData.buyerCity || '',
      buyer_company_name: formData.buyerCompanyName || '',
      buyer_title: formData.buyerTitle || 'Mr.',
      buyer_country: formData.buyerCountry || '',
      buyer_email_address: formData.buyerEmail || '',
      buyer_telephone_no: formData.buyerPhone1 || '',
      buyer_id: formData.buyerId || '',
      from_country: formData.fromCountry || 'JP',
      from_port: formData.fromPort || '',
      to_country: formData.toCountry || '',
      to_port: formData.toPort || '',
      vehicles: vehicles,
      items: items,
      charges: charges,
      discount_percent: formData.discountPercent || 0,
      ship_type: formData.shipType || 'roro',
      total_type: formData.totalType || 'c_and_f',
      agent_notes: formData.agentNotes || '',
      payment_terms: formData.paymentTerms || '',
      same_as_consignee: formData.sameAsConsignee || false
    };
  }

  private getMonthName(monthValue: string): string {
    const monthMap: { [key: string]: string } = {
      '1': 'january', '2': 'february', '3': 'march', '4': 'april',
      '5': 'may', '6': 'june', '7': 'july', '8': 'august',
      '9': 'september', '10': 'october', '11': 'november', '12': 'december'
    };
    return monthMap[monthValue] || 'january';
  }

  private showValidationErrorAlert(invalidFields: string[]): void {
    if (invalidFields.length > 0) {
      const message = `Please fill in the following required fields:\n\n• ${invalidFields.join('\n• ')}`;
      Swal.fire({
        text: message,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
    } else {
      Swal.fire({
        text: "Please check the form for validation errors.",
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
    }
  }

  private validateVehicles(): boolean {
    if (this.vehicles.length === 0) {
      return false;
    }

    return this.vehicles.every((vehicle) => {
      return vehicle.stockMake &&
        vehicle.stockModel &&
        vehicle.stockChassisNo &&
        vehicle.stockMFYear &&
        vehicle.stockRegYear &&
        vehicle.stockFuel &&
        vehicle.stockExteriorColor;
    });
  }

  private validateItems(): boolean {
    if (this.items.length === 0) {
      return false;
    }

    return this.items.every(item => {
      return item.description &&
        item.quantity &&
        item.currency;
      // Note: amount is optional as per requirements
    });
  }

  private getInvalidFields(): string[] {
    const invalidFields: string[] = [];
    const controls = this.proformaForm.controls;

    // Check each form control
    Object.keys(controls).forEach(key => {
      const control = controls[key];
      if (control.invalid && control.errors) {
        // Map form control names to user-friendly names
        const fieldName = this.getFieldDisplayName(key);
        if (control.errors['required']) {
          invalidFields.push(fieldName);
        }
      }
    });

    return invalidFields;
  }

  private getInvalidVehicleFields(): string[] {
    const invalidFields: string[] = [];

    this.vehicles.forEach((vehicle, index) => {
      const vehiclePrefix = `Vehicle ${index + 1}`;

      if (!vehicle.stockMake) {
        invalidFields.push(`${vehiclePrefix} - Make`);
      }
      if (!vehicle.stockModel) {
        invalidFields.push(`${vehiclePrefix} - Model`);
      }
      if (!vehicle.stockChassisNo) {
        invalidFields.push(`${vehiclePrefix} - Chassis No.`);
      }
      if (!vehicle.stockMFYear) {
        invalidFields.push(`${vehiclePrefix} - Manufacturing Year`);
      }
      if (!vehicle.stockRegYear) {
        invalidFields.push(`${vehiclePrefix} - Registration Year`);
      }
      if (!vehicle.stockFuel) {
        invalidFields.push(`${vehiclePrefix} - Fuel`);
      }
      if (!vehicle.stockExteriorColor) {
        invalidFields.push(`${vehiclePrefix} - Exterior Color`);
      }
    });

    return invalidFields;
  }

  private getInvalidItemFields(): string[] {
    const invalidFields: string[] = [];

    this.items.forEach((item, index) => {
      const itemPrefix = `Item ${index + 1}`;

      if (!item.description) {
        invalidFields.push(`${itemPrefix} - Description`);
      }
      if (!item.quantity) {
        invalidFields.push(`${itemPrefix} - Quantity`);
      }
      if (!item.currency) {
        invalidFields.push(`${itemPrefix} - Currency`);
      }
      // Note: amount is optional as per requirements
    });

    return invalidFields;
  }

  private getFieldDisplayName(controlName: string): string {
    const fieldNames: { [key: string]: string } = {
      'buyerCompanyName': 'Buyer Company Name',
      'buyerTitle': 'Buyer Title',
      'buyerName': 'Buyer Name',
      'buyerAddress': 'Buyer Address',
      'buyerCity': 'Buyer City',
      'buyerCountry': 'Buyer Country',
      'buyerEmail': 'Buyer Email',
      'buyerPhone1': 'Buyer Phone',
      'toCountry': 'To Country',
      'toPort': 'To Port',
      'paymentTerms': 'Payment Terms',
      'shipType': 'Ship Type',
      'totalType': 'Total Type'
    };

    return fieldNames[controlName] || controlName;
  }

  // private showMissingFieldsAlert(invalidFields: string[]): void {
  //   if (invalidFields.length > 0) {
  //     const message = `Please fill in the following required fields:\n\n• ${invalidFields.join('\n• ')}`;
  //     alert(message);
  //   } else {
  //     alert('Please check the form for validation errors.');
  //   }
  // }

  // Helper methods for template validation display
  isFieldInvalid(fieldName: string): boolean {
    const field = this.proformaForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched || this.isFormSubmitted) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.proformaForm.get(fieldName);
    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
    }
    return '';
  }

  // Helper methods for vehicle validation
  isVehicleFieldInvalid(vehicleIndex: number, fieldName: string): boolean {
    if (!this.isFormSubmitted) return false;

    switch (fieldName) {
      case 'make':
        return !this.vehicles[vehicleIndex]?.stockMake;
      case 'model':
        return !this.vehicles[vehicleIndex]?.stockModel;
      case 'chassisNo':
        return !this.vehicles[vehicleIndex]?.stockChassisNo;
      case 'mfYear':
        return !this.vehicles[vehicleIndex]?.stockMFYear;
      case 'regYear':
        return !this.vehicles[vehicleIndex]?.stockRegYear;
      case 'fuel':
        return !this.vehicles[vehicleIndex]?.stockFuel;
      case 'exteriorColor':
        return !this.vehicles[vehicleIndex]?.stockExteriorColor;
      default:
        return false;
    }
  }

  // Helper methods for item validation
  isItemFieldInvalid(itemIndex: number, fieldName: string): boolean {
    if (!this.isFormSubmitted) return false;

    const item = this.items[itemIndex];
    if (!item) return false;

    switch (fieldName) {
      case 'description':
        return !item.description;
      case 'quantity':
        return !item.quantity || item.quantity <= 0;
      case 'currency':
        return !item.currency;
      default:
        return false;
    }
  }

  ngAfterViewInit(): void {
    // Initialize with one vehicle (don't call addVehicle as it will add an extra item)
    // this.vehicles.push(this.getEmptyVehicle());
  }
}
